import ReactApexChart from "react-apexcharts";
import { useMemo } from "react";

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

// Indian number shortener: 1200000 → ₹12L, 100000 → ₹1L, etc.
const formatINR = (val) => {
  if (!val || val === 0) return "₹0";
  if (val >= 10000000) return `₹${(val / 10000000).toFixed(1)}Cr`;
  if (val >= 100000)  return `₹${(val / 100000).toFixed(1)}L`;
  if (val >= 1000)    return `₹${(val / 1000).toFixed(1)}K`;
  return `₹${val}`;
};

function LineChart({ data = [], isManager = true }) {
  const maxVal  = Math.max(...(data || []), 1);
  const avgVal  = data?.length ? Math.round(data.reduce((a, b) => a + b, 0) / data.length) : 0;
  const peakIdx = data?.indexOf(maxVal) ?? -1;
  const totalYear = (data || []).reduce((a, b) => a + b, 0);

  const series = [{
    name: "Payments",
    data: data || [],
  }];

  const options = useMemo(() => ({
    chart: {
      type: "area",
      height: 240,
      toolbar: { show: false },
      zoom: { enabled: false },
      animations: { enabled: true, speed: 700 },
      background: "transparent",
    },
    dataLabels: { enabled: false },
    stroke: {
      curve: "smooth",
      width: 3,
      colors: ["#52c41a"],
    },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 1,
        opacityFrom: 0.45,
        opacityTo: 0.02,
        stops: [0, 95],
        colorStops: [{
          offset: 0,
          color: "#52c41a",
          opacity: 0.45,
        }, {
          offset: 95,
          color: "#52c41a",
          opacity: 0.02,
        }],
      },
    },
    markers: {
      size: 4,
      colors: ["#52c41a"],
      strokeColors: "#fff",
      strokeWidth: 2,
      hover: { size: 7 },
    },
    grid: {
      borderColor: "#f0f0f0",
      strokeDashArray: 4,
      xaxis: { lines: { show: false } },
    },
    xaxis: {
      categories: MONTHS,
      labels: {
        style: { colors: "#8c8c8c", fontSize: "12px", fontWeight: 500 },
      },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      labels: {
        style: { colors: "#8c8c8c", fontSize: "12px" },
        formatter: (v) => formatINR(v),
      },
    },
    annotations: {
      yaxis: avgVal > 0 ? [{
        y: avgVal,
        borderColor: "#1890ff",
        borderWidth: 1.5,
        strokeDashArray: 4,
        label: {
          text: `Avg ${formatINR(avgVal)}`,
          style: { color: "#1890ff", background: "#e6f7ff", fontSize: "11px", fontWeight: 600 },
          position: "right",
          offsetX: -10,
        },
      }] : [],
      points: peakIdx >= 0 && maxVal > 0 ? [{
        x: MONTHS[peakIdx],
        y: maxVal,
        marker: { size: 6, fillColor: "#faad14", strokeColor: "#fff", strokeWidth: 2 },
        label: {
          text: `Peak ${formatINR(maxVal)}`,
          style: { color: "#fff", background: "#faad14", fontSize: "10px" },
          offsetY: -10,
        },
      }] : [],
    },
    tooltip: {
      theme: "light",
      y: {
        formatter: (val) => `₹${Number(val).toLocaleString("en-IN")}`,
        title: { formatter: () => "" },
      },
    },
    legend: { show: false },
  }), [data]);

  return (
    <div>
      {/* Summary row (only shown to manager/accountant) */}
      {isManager && (
        <div style={{ display: "flex", gap: "24px", marginBottom: "8px", flexWrap: "wrap" }}>
          <div>
            <span style={{ fontSize: "11px", color: "#8c8c8c" }}>Year Total</span>
            <div style={{ fontSize: "18px", fontWeight: 700, color: "#52c41a" }}>
              ₹{Number(totalYear).toLocaleString("en-IN")}
            </div>
          </div>
          {peakIdx >= 0 && maxVal > 0 && (
            <div>
              <span style={{ fontSize: "11px", color: "#8c8c8c" }}>Peak Month</span>
              <div style={{ fontSize: "18px", fontWeight: 700, color: "#faad14" }}>
                {MONTHS[peakIdx]} ({formatINR(maxVal)})
              </div>
            </div>
          )}
          {avgVal > 0 && (
            <div>
              <span style={{ fontSize: "11px", color: "#8c8c8c" }}>Monthly Avg</span>
              <div style={{ fontSize: "18px", fontWeight: 700, color: "#1890ff" }}>
                {formatINR(avgVal)}
              </div>
            </div>
          )}
        </div>
      )}
      <ReactApexChart options={options} series={series} type="area" height={220} width="100%" />
    </div>
  );
}

export default LineChart;
